import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import debounce from 'lodash/debounce'
import { useScreenSize } from 'common/hooks/useScreenSize'

type useBreadcrumbsMeasureProps = {
  itemsCount: number
}

export const useBreadcrumbsMeasure = ({
  itemsCount,
}: useBreadcrumbsMeasureProps) => {
  const ref = useRef<HTMLDivElement | null>(null)
  const measureRef = useRef<HTMLDivElement | null>(null)
  const screenSize = useScreenSize()
  const [allowedItemsCount, setAllowedItemsCount] = useState<number | null>(
    null,
  )

  const getFittingItemsCount = useCallback(
    (parentWidth: number, childrenWidths: number[]) => {
      let total = 0
      let count = 0
      for (const width of childrenWidths) {
        if (total + width > parentWidth) break
        total += width
        count++
      }
      return count
    },
    [],
  )

  const measure = useCallback(() => {
    if (!measureRef.current || itemsCount === 0) return
    const parent = ref.current?.parentElement
    if (!parent) return

    const parentWidth = parent.offsetWidth
    const childrenWidths = Array.from(measureRef.current.childNodes).map(
      item => (item as HTMLElement).offsetWidth,
    )
    const totalWidth = childrenWidths.reduce((acc, w) => acc + w, 0)

    if (totalWidth > parentWidth) {
      setAllowedItemsCount(getFittingItemsCount(parentWidth, childrenWidths))
    } else {
      setAllowedItemsCount(itemsCount)
    }
  }, [itemsCount, getFittingItemsCount])

  const debouncedMeasure = useMemo(() => debounce(measure, 100), [measure])

  useEffect(() => {
    measure()
  }, [itemsCount, screenSize.width, measure])

  useEffect(() => {
    debouncedMeasure()
    return () => debouncedMeasure.cancel()
  }, [screenSize.width])

  const setRef = useCallback((node: HTMLDivElement | null) => {
    ref.current = node
  }, [])
  const setMeasureRef = useCallback((node: HTMLDivElement | null) => {
    measureRef.current = node
  }, [])

  return {
    setRef,
    setMeasureRef,
    allowedItemsCount,
  }
}
